import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Zap, Star, Award, X } from 'lucide-react';

const ratingConfigs = {
  again: {
    label: 'Repetir',
    color: 'bg-rose-600',
    accent: 'text-rose-200',
    delay: 1,
    xp: 2
  },
  hard: {
    label: 'Difícil',
    color: 'bg-amber-600',
    accent: 'text-amber-200',
    delay: 4,
    xp: 5
  },
  good: {
    label: 'Bien',
    color: 'bg-emerald-600',
    accent: 'text-emerald-200',
    delay: 8,
    xp: 10
  },
  easy: {
    label: 'Perfecto',
    color: 'bg-cyan-600',
    accent: 'text-cyan-200',
    delay: null,
    xp: 15
  }
};

const xpForLevel = (level) => 100 + level * 40;

const loadDeckStats = (deckId) => {
  try {
    const stored = localStorage.getItem(`flashcard_practice_stats_${deckId}`);
    if (!stored) {
      return { xp: 0, level: 1, streak: 0, totalReviewed: 0 };
    }
    return JSON.parse(stored);
  } catch {
    return { xp: 0, level: 1, streak: 0, totalReviewed: 0 };
  }
};

const saveDeckStats = (deckId, stats) => {
  localStorage.setItem(`flashcard_practice_stats_${deckId}`, JSON.stringify(stats));
};

const createQueue = (cards = []) => {
  const shuffled = [...cards].sort(() => Math.random() - 0.5);
  return shuffled.map((card) => ({
    ...card,
    lapses: 0,
    ease: 2.5
  }));
};

const FlashcardPractice = ({ deck, cards, onClose, darkMode }) => {
  const [queue, setQueue] = useState(() => createQueue(cards));
  const [currentIndex, setCurrentIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [sessionStats, setSessionStats] = useState({
    reviewed: 0,
    perfects: 0,
    combo: 0,
    bestCombo: 0
  });
  const [deckStats, setDeckStats] = useState(() => loadDeckStats(deck?.id));
  const [celebration, setCelebration] = useState(null);
  const [sessionComplete, setSessionComplete] = useState(false);

  useEffect(() => {
    setQueue(createQueue(cards));
    setCurrentIndex(0);
    setRevealed(false);
    setSessionStats({
      reviewed: 0,
      perfects: 0,
      combo: 0,
      bestCombo: 0
    });
    setSessionComplete(false);
  }, [cards]);

  useEffect(() => {
    setDeckStats(loadDeckStats(deck?.id));
  }, [deck?.id]);

  const currentCard = useMemo(() => queue[currentIndex], [queue, currentIndex]);

  const remaining = queue.length - currentIndex - (revealed ? 0 : 1);

  const handleReveal = () => setRevealed(true);

  const handleRating = useCallback(
    (ratingKey) => {
      const rating = ratingConfigs[ratingKey];
      if (!currentCard) return;

      setSessionStats((prev) => {
        const reviewed = prev.reviewed + 1;
        const combo = ratingKey === 'easy' ? prev.combo + 1 : 0;
        const bestCombo = Math.max(prev.bestCombo, combo);
        return {
          reviewed,
          perfects: ratingKey === 'easy' ? prev.perfects + 1 : prev.perfects,
          combo,
          bestCombo
        };
      });

      setDeckStats((prev) => {
        const updatedXp = prev.xp + rating.xp;
        let { level } = prev;
        let xp = updatedXp;
        const xpNeeded = xpForLevel(level);
        if (updatedXp >= xpNeeded) {
          level += 1;
          xp = updatedXp - xpNeeded;
          setCelebration({
            type: 'level',
            level
          });
        } else if (ratingKey === 'easy') {
          setCelebration({
            type: 'perfect',
            points: rating.xp
          });
        } else {
          setCelebration(null);
        }
        const stats = {
          xp,
          level,
          streak: ratingKey === 'easy' ? prev.streak + 1 : 0,
          totalReviewed: prev.totalReviewed + 1
        };
        saveDeckStats(deck.id, stats);
        return stats;
      });

      const nextQueue = [...queue];
      nextQueue.splice(currentIndex, 1);

      if (rating.delay !== null) {
        const insertIndex = Math.min(
          currentIndex + rating.delay,
          nextQueue.length
        );
        nextQueue.splice(insertIndex, 0, {
          ...currentCard,
          lapses: ratingKey === 'again' ? currentCard.lapses + 1 : currentCard.lapses,
          lastRating: ratingKey
        });
      }

      if (!nextQueue.length) {
        setSessionComplete(true);
      } else {
        setCurrentIndex((prev) => (prev >= nextQueue.length ? 0 : prev));
      }

      setQueue(nextQueue);
      setRevealed(false);
    },
    [currentCard, deck?.id, queue, currentIndex]
  );

  const progressPercent = useMemo(() => {
    if (!cards.length) return 0;
    const completed = cards.length - remaining - (revealed ? 0 : 1);
    return Math.max(0, Math.min(100, (completed / cards.length) * 100));
  }, [cards.length, remaining, revealed]);

  if (!deck) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-md bg-black/70">
      <div className={`relative w-full max-w-5xl mx-4 rounded-3xl overflow-hidden ${darkMode ? 'bg-slate-900 text-white' : 'bg-white text-slate-900'} shadow-2xl`}>
        <button
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/30 hover:bg-black/50 text-white transition"
          onClick={onClose}
        >
          <X className="w-5 h-5" />
        </button>

        <div className={`p-6 border-b ${darkMode ? 'border-slate-800' : 'border-slate-200'} flex flex-wrap gap-4 items-center`}>
          <div>
            <p className="text-sm opacity-70 uppercase tracking-wide">Practicando</p>
            <h2 className="text-2xl font-bold">{deck.name}</h2>
          </div>
          <div className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-cyan-400 to-emerald-400"
              initial={{ width: 0 }}
              animate={{ width: `${progressPercent}%` }}
              transition={{ type: 'spring', duration: 0.6 }}
            />
          </div>
          <div className="flex gap-4">
            <StatPill
              icon={<Sparkles className="w-4 h-4" />}
              label="Nivel"
              value={`Lv.${deckStats.level}`}
              accent="bg-purple-500/20 text-purple-200"
            />
            <StatPill
              icon={<Zap className="w-4 h-4" />}
              label="Streak"
              value={`${deckStats.streak}🔥`}
              accent="bg-amber-500/20 text-amber-200"
            />
            <StatPill
              icon={<Star className="w-4 h-4" />}
              label="Combo"
              value={`${sessionStats.combo}x`}
              accent="bg-cyan-500/20 text-cyan-200"
            />
          </div>
        </div>

        <div className="p-8 flex flex-col gap-6">
          <AnimatePresence mode="wait">
            {sessionComplete ? (
              <motion.div
                key="complete"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="text-center space-y-4"
              >
                <Award className="w-16 h-16 text-emerald-400 mx-auto" />
                <h3 className="text-3xl font-bold">Sesión completada 🎉</h3>
                <p className="opacity-80">
                  Repasaste {sessionStats.reviewed} tarjetas y obtuviste {deckStats.xp} XP para este deck.
                </p>
                <button
                  className="px-6 py-3 rounded-full bg-gradient-to-r from-cyan-500 to-emerald-500 text-white font-semibold"
                  onClick={() => {
                    setQueue(createQueue(cards));
                    setSessionComplete(false);
                    setCurrentIndex(0);
                    setRevealed(false);
                  }}
                >
                  Reiniciar sesión
                </button>
              </motion.div>
            ) : (
              currentCard && (
                <motion.div
                  key={currentCard.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="grid gap-6 lg:grid-cols-2"
                >
                  <motion.div
                    className={`rounded-3xl p-8 min-h-[280px] relative overflow-hidden ${darkMode ? 'bg-slate-800 border border-slate-700' : 'bg-slate-50 border border-slate-200'}`}
                    whileHover={{ scale: 1.01 }}
                  >
                    <div className="absolute inset-0 opacity-5 bg-[radial-gradient(circle_at_top,_#38bdf8,_transparent_60%)]" />
                    <p className="text-sm uppercase tracking-wide opacity-60 mb-2">{deck.area || 'Flashcard'}</p>
                    <h3 className="text-2xl font-bold">{currentCard.front}</h3>
                    {!revealed && (
                      <button
                        onClick={handleReveal}
                        className="absolute bottom-6 right-6 px-4 py-2 rounded-full bg-cyan-500 text-white font-semibold"
                      >
                        Mostrar respuesta
                      </button>
                    )}
                  </motion.div>
                  <motion.div
                    className={`rounded-3xl p-8 min-h-[280px] border-dashed border-2 ${revealed ? 'border-emerald-400' : 'border-slate-400/50'} ${darkMode ? 'bg-slate-900/60' : 'bg-white/60'}`}
                    animate={{ opacity: revealed ? 1 : 0.3, filter: revealed ? 'blur(0px)' : 'blur(8px)' }}
                  >
                    <p className="text-sm uppercase tracking-wide opacity-60 mb-2">Respuesta</p>
                    <p className="text-xl font-semibold">
                      {revealed ? currentCard.back : 'Desliza para revelar'}
                    </p>
                    {revealed && currentCard.mnemonic && (
                      <div className="mt-4 p-4 rounded-2xl bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">
                        <p className="text-sm uppercase tracking-wide opacity-80">Mnemotecnia</p>
                        <p className="font-semibold">{currentCard.mnemonic}</p>
                      </div>
                    )}
                  </motion.div>
                </motion.div>
              )
            )}
          </AnimatePresence>

          {!sessionComplete && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(ratingConfigs).map(([key, config]) => (
                <button
                  key={key}
                  disabled={!revealed}
                  onClick={() => handleRating(key)}
                  className={`py-4 rounded-2xl text-center font-semibold text-white shadow-lg transition transform hover:-translate-y-1 disabled:opacity-40 ${config.color}`}
                >
                  {config.label}
                  <p className={`text-xs mt-1 ${config.accent}`}>{config.xp} XP</p>
                </button>
              ))}
            </div>
          )}
        </div>

        <AnimatePresence>
          {celebration && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-x-0 bottom-8 flex justify-center pointer-events-none"
            >
              <div className="px-6 py-3 rounded-full bg-gradient-to-r from-yellow-400 to-pink-500 text-white font-semibold shadow-xl flex items-center gap-3">
                <Sparkles className="w-5 h-5" />
                {celebration.type === 'level'
                  ? `¡Nivel ${celebration.level}!`
                  : `+${celebration.points} XP · Combo ${sessionStats.combo}x`}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

const StatPill = ({ icon, label, value, accent }) => (
  <div className={`px-4 py-2 rounded-2xl flex items-center gap-2 ${accent}`}>
    {icon}
    <div>
      <p className="text-xs uppercase tracking-wide opacity-70">{label}</p>
      <p className="text-sm font-semibold">{value}</p>
    </div>
  </div>
);

export default FlashcardPractice;
